// Job History Management Utility
// Handles localStorage persistence for async job creation monitoring

export interface JobHistoryEntry {
  id: string;
  name: string;
  storeId: string;
  storeName: string;
  productCount: number;
  createdAt: string;
  status: 'creating' | 'completed' | 'failed';
  batchId?: string;
  completedAt?: string;
  failedReason?: string;
}

const JOB_HISTORY_KEY = 'imagex_job_history';
const CURRENT_JOB_KEY = 'imagex_current_job';
const MAX_HISTORY_ENTRIES = 10; // Keep last 10 jobs

class JobHistoryManager {
  // Get all job history
  getJobHistory(): JobHistoryEntry[] {
    try {
      const history = localStorage.getItem(JOB_HISTORY_KEY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Failed to load job history:', error);
      return [];
    }
  }

  // Add a new job to history
  addJob(job: Omit<JobHistoryEntry, 'createdAt' | 'status'>): JobHistoryEntry {
    const newJob: JobHistoryEntry = {
      ...job,
      createdAt: new Date().toISOString(),
      status: 'creating'
    };

    try {
      const history = this.getJobHistory();
      
      // Remove any existing job with same ID (in case of retry)
      const filteredHistory = history.filter(j => j.id !== job.id);
      
      // Add new job at the beginning
      const updatedHistory = [newJob, ...filteredHistory];
      
      // Keep only the latest entries
      const trimmedHistory = updatedHistory.slice(0, MAX_HISTORY_ENTRIES);
      
      localStorage.setItem(JOB_HISTORY_KEY, JSON.stringify(trimmedHistory));
      this.setCurrentJob(newJob.id);
      
      return newJob;
    } catch (error) {
      console.error('Failed to add job to history:', error);
      return newJob;
    }
  }

  // Update job status
  updateJobStatus(
    jobId: string, 
    status: JobHistoryEntry['status'], 
    updates: Partial<Pick<JobHistoryEntry, 'batchId' | 'completedAt' | 'failedReason'>> = {}
  ): void {
    try {
      const history = this.getJobHistory();
      const updatedHistory = history.map(job => {
        if (job.id === jobId) {
          const updatedJob = {
            ...job,
            status,
            ...updates
          };

          if (status === 'completed') {
            updatedJob.completedAt = new Date().toISOString();
          }

          return updatedJob;
        }
        return job;
      });

      localStorage.setItem(JOB_HISTORY_KEY, JSON.stringify(updatedHistory));

      // Clear current job if this was it and it's now completed/failed
      if ((status === 'completed' || status === 'failed') && this.getCurrentJobId() === jobId) {
        this.clearCurrentJob();
      }
    } catch (error) {
      console.error('Failed to update job status:', error);
    }
  }

  // Get current active job ID
  getCurrentJobId(): string | null {
    try {
      return localStorage.getItem(CURRENT_JOB_KEY);
    } catch (error) {
      console.error('Failed to get current job ID:', error);
      return null;
    }
  }

  // Set current active job ID
  setCurrentJob(jobId: string): void {
    try {
      localStorage.setItem(CURRENT_JOB_KEY, jobId);
    } catch (error) {
      console.error('Failed to set current job ID:', error);
    }
  }

  // Clear current job
  clearCurrentJob(): void {
    try {
      localStorage.removeItem(CURRENT_JOB_KEY);
    } catch (error) {
      console.error('Failed to clear current job ID:', error);
    }
  }

  // Get job by ID
  getJob(jobId: string): JobHistoryEntry | null {
    const history = this.getJobHistory();
    return history.find(job => job.id === jobId) || null;
  }

  // Get recent jobs (completed/failed only)
  getRecentJobs(limit: number = 5): JobHistoryEntry[] {
    const history = this.getJobHistory();
    return history
      .filter(job => job.status === 'completed' || job.status === 'failed')
      .slice(0, limit);
  }

  // Get currently creating jobs
  getActiveJobs(): JobHistoryEntry[] {
    const history = this.getJobHistory();
    return history.filter(job => job.status === 'creating');
  }

  // Clean old completed jobs (keep only recent ones)
  cleanHistory(): void {
    try {
      const history = this.getJobHistory();
      const cleaned = history.slice(0, MAX_HISTORY_ENTRIES);
      localStorage.setItem(JOB_HISTORY_KEY, JSON.stringify(cleaned));
    } catch (error) {
      console.error('Failed to clean job history:', error);
    }
  }

  // Remove specific job from history
  removeJob(jobId: string): void {
    try {
      const history = this.getJobHistory();
      const filteredHistory = history.filter(job => job.id !== jobId);
      localStorage.setItem(JOB_HISTORY_KEY, JSON.stringify(filteredHistory));

      // Clear current job if this was it
      if (this.getCurrentJobId() === jobId) {
        this.clearCurrentJob();
      }
    } catch (error) {
      console.error('Failed to remove job from history:', error);
    }
  }

  // Format job name for display
  formatJobName(job: JobHistoryEntry): string {
    const date = new Date(job.createdAt).toLocaleDateString();
    const time = new Date(job.createdAt).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    return `${job.storeName} - ${job.productCount} products (${date} ${time})`;
  }

  // Get status display info
  getStatusDisplay(status: JobHistoryEntry['status']): { 
    label: string; 
    color: string; 
    icon: string; 
  } {
    switch (status) {
      case 'creating':
        return {
          label: 'Creating Jobs...',
          color: 'text-blue-600 bg-blue-100',
          icon: '⏳'
        };
      case 'completed':
        return {
          label: 'Completed',
          color: 'text-green-600 bg-green-100',
          icon: '✅'
        };
      case 'failed':
        return {
          label: 'Failed',
          color: 'text-red-600 bg-red-100',
          icon: '❌'
        };
      default:
        return {
          label: 'Unknown',
          color: 'text-gray-600 bg-gray-100',
          icon: '❓'
        };
    }
  }
}

// Export singleton instance
export const jobHistoryManager = new JobHistoryManager();

// Export utility functions for convenience
export const {
  getJobHistory,
  addJob,
  updateJobStatus,
  getCurrentJobId,
  setCurrentJob,
  clearCurrentJob,
  getJob,
  getRecentJobs,
  getActiveJobs,
  cleanHistory,
  removeJob,
  formatJobName,
  getStatusDisplay
} = jobHistoryManager;